import { useAppStore } from '../store';
import { motion } from 'framer-motion';
import { BookOpen, Target, HelpCircle, ChevronDown, Lightbulb, CheckSquare } from 'lucide-react';
import { useState } from 'react';

export default function PreviewPage() {
  const { units, activeSubjectId } = useAppStore();
  const subjectUnits = units.filter(u => u.subjectId === activeSubjectId);
  const [expandedUnit, setExpandedUnit] = useState<string>(subjectUnits[0]?.id || '');

  const previewData = subjectUnits.map(unit => ({
    unitId: unit.id,
    title: unit.title,
    objectives: [
      `理解${unit.title}的基本概念和关键术语`,
      `掌握本单元中 IB 考试重点内容`,
      `能够运用所学知识解释相关生物学现象`,
      `培养科学探究与批判性思维能力`,
    ],
    framework: `本单元共 ${unit.knowledgePoints.length} 个知识点：${unit.knowledgePoints.map(kp => kp.title).join(' → ')}`,
    thinkingQuestions: [
      `你能用自己的话解释"${unit.knowledgePoints[0]?.title}"吗？`,
      `${unit.title}中哪些概念之间存在因果关系？`,
      `这些知识在日常生活中有哪些应用？`,
      `如果考试中遇到这个话题的 8 分题，你会如何组织答案？`,
    ],
  }));

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl lg:text-4xl font-display text-stone-950 tracking-tight">预习模块</h1>
        <p className="text-sm text-stone-400 mt-2 font-body">明确学习目标，构建知识框架，带着问题去学习</p>
      </motion.div>

      {previewData.map((preview, idx) => {
        const isExpanded = expandedUnit === preview.unitId;
        return (
          <motion.div
            key={preview.unitId}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.08 }}
            className="card-premium overflow-hidden"
          >
            <button
              onClick={() => setExpandedUnit(isExpanded ? '' : preview.unitId)}
              className="w-full flex items-center justify-between p-5 text-left hover:bg-linen/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-steel-50 flex items-center justify-center">
                  <BookOpen className="w-4 h-4 text-steel" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-stone-950 font-body">{preview.title}</h3>
                  <p className="text-[10px] text-stone-400 font-body">单元 {idx + 1}</p>
                </div>
              </div>
              <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
            </button>

            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                className="px-4 sm:px-5 pb-4 sm:pb-5 space-y-4 sm:space-y-5"
              >
                <div className="bg-sage-50 rounded-2xl p-4 sm:p-5">
                  <h4 className="text-xs font-semibold text-sage-dark mb-2.5 flex items-center gap-2 font-body">
                    <Target className="w-3.5 h-3.5" /> 预习目标
                  </h4>
                  <div className="space-y-2">
                    {preview.objectives.map((obj, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-stone-600 font-body">
                        <CheckSquare className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-sage" />
                        {obj}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-steel-50 rounded-2xl p-4 sm:p-5">
                  <h4 className="text-xs font-semibold text-steel mb-2.5 flex items-center gap-2 font-body">
                    <Lightbulb className="w-3.5 h-3.5" /> 知识框架
                  </h4>
                  <p className="text-xs text-stone-600 leading-relaxed font-body">{preview.framework}</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {subjectUnits.find(u => u.id === preview.unitId)?.knowledgePoints.map(kp => (
                      <span key={kp.id} className={`text-[10px] px-2.5 py-1 rounded-lg border font-body diff-${kp.difficulty}`}>
                        {kp.title}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="bg-cedar-50 rounded-2xl p-4 sm:p-5">
                  <h4 className="text-xs font-semibold text-cedar mb-2.5 flex items-center gap-2 font-body">
                    <HelpCircle className="w-3.5 h-3.5" /> 预习思考题
                  </h4>
                  <div className="space-y-2.5">
                    {preview.thinkingQuestions.map((q, i) => (
                      <div key={i} className="flex items-start gap-2.5 text-xs text-stone-600 font-body">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-bronze/15 text-cedar flex items-center justify-center text-[10px] font-bold">{i + 1}</span>
                        {q}
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        );
      })}
    </div>
  );
}
